/* eslint-disable @typescript-eslint/no-explicit-any */
import apiClient, { type ExtendedAxiosRequestConfig } from './api';
import type { Achievement, Challenge, LeaderboardEntry } from '../types';

// ─── Normalizers ──────────────────────────────────────────────────────────────

const BADGE_ICON_MAP: Record<string, string> = {
  NUTRITION: '🥗', FITNESS: '💪', CONSISTENCY: '🔥', SOCIAL: '👥', MILESTONE: '🏅',
};

function normalizeBadge(b: any): Achievement {
  const isUnlocked = b.is_unlocked ?? false;
  return {
    id: String(b.id),
    name: b.name,
    description: b.description,
    icon: b.icon || BADGE_ICON_MAP[b.category] || '🏅',
    unlockedAt: isUnlocked ? (b.unlocked_at ?? new Date().toISOString()) : undefined,
    progress: isUnlocked ? b.criteria_value : 0,
    total: b.criteria_value,
  };
}

function normalizeChallenge(c: any): Challenge {
  return {
    id: String(c.id),
    title: c.title,
    description: c.description,
    type: c.challenge_type?.includes('WEEK') ? 'weekly' : c.challenge_type?.includes('MONTH') ? 'monthly' : 'daily',
    target: c.target_value,
    current: c.current_value ?? 0,
    reward: `${c.reward_points} points`,
    startDate: c.start_date,
    endDate: c.end_date,
    isCompleted: c.is_completed ?? false,
  };
}

function normalizeLeaderboardEntry(e: any): LeaderboardEntry {
  return {
    rank: e.rank,
    userId: String(e.user_id),
    userName: e.user_name || 'User',
    userAvatar: e.user_avatar || undefined,
    points: e.points,
    streak: e.streak_days ?? 0,
  };
}

// ─── API ──────────────────────────────────────────────────────────────────────

const skipRedirect: ExtendedAxiosRequestConfig = { _skipAuthRedirect: true };

export const gamificationApi = {
  getBadges: async (): Promise<Achievement[]> => {
    const response = await apiClient.get('/gamification/badges', skipRedirect);
    const raw: any[] = Array.isArray(response.data) ? response.data : [];
    return raw.map(normalizeBadge);
  },

  getChallenges: async (): Promise<Challenge[]> => {
    const response = await apiClient.get('/gamification/challenges', skipRedirect);
    const raw: any[] = Array.isArray(response.data) ? response.data : [];
    return raw.map(normalizeChallenge);
  },

  joinChallenge: async (challengeId: string): Promise<Challenge> => {
    const response = await apiClient.post(`/gamification/challenges/${challengeId}/join`);
    return normalizeChallenge(response.data);
  },

  // Backend returns LeaderboardResponse { period, entries, user_rank, user_points }
  getLeaderboard: async (period: 'weekly' | 'monthly' | 'all_time' = 'weekly'): Promise<LeaderboardEntry[]> => {
    // Backend accepts: weekly, monthly, alltime (not all_time)
    const backendPeriod = period === 'all_time' ? 'alltime' : period;
    const response = await apiClient.get('/gamification/leaderboard', {
      params: { period: backendPeriod },
      _skipAuthRedirect: true,
    } as ExtendedAxiosRequestConfig);
    const data = response.data;
    const raw: any[] = data.entries ?? (Array.isArray(data) ? data : []);
    return raw.map(normalizeLeaderboardEntry);
  },

  // Backend returns { current_streak, longest_streak, total_points, last_active_date }
  getStreak: async (): Promise<{ current: number; longest: number; lastActiveDate: string }> => {
    const response = await apiClient.get('/gamification/streak', skipRedirect);
    const d = response.data;
    return {
      current: d.current_streak ?? 0,
      longest: d.longest_streak ?? 0,
      lastActiveDate: d.last_active_date ?? '',
    };
  },
};
